import {useState, useMemo, useRef, useCallback, useLayoutEffect, Fragment} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {useWindowVirtualizer} from '@tanstack/react-virtual';
import {Button, Box, Input, Table, Sheet, Skeleton, Checkbox} from '@mui/joy';
import AddIcon from "@mui/icons-material/Add";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {EditAction} from './EditAction';
import {DeleteAction} from './DeleteAction';
import {ExtraAction} from './ExtraAction';
import SearchIcon from '@mui/icons-material/Search';
import {capitalizeFirstLetter} from '@/utils/strings';
import {ApiError} from "@/components/ApiError.jsx";

const defaultOptions = {
  searchable: true,
  header: true,
}

export function CrudTable({names, columns, actions, options, externalSearch, list, selection }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState(options?.defaultSort?.column ?? null);
  const [sortOrder, setSortOrder] = useState(options?.defaultSort?.order ?? 'asc');

  const {isLoading, error, data} = actions.list ? actions.list() : list;

  const rows = data?.data ?? [];
  // getter renders the cell (often JSX -- an editable input, a badge, a link),
  // so it isn't safe to use as plain text. textValue is the optional
  // plain-string override for columns whose getter doesn't already return one;
  // falling back to getter itself only works for the (common) case where a
  // column's display value already is a plain string or number.
  const primaryTextValue = columns[0].textValue ?? columns[0].getter;
  const filteredRows = useMemo(() => {
    const searchTerm = (options?.externalSearch ? externalSearch : search).toLowerCase();
    return rows.filter((row) => {
      const value = (primaryTextValue(row) + '').toLowerCase();
      return value.includes(searchTerm);
    });
  }, [rows, primaryTextValue, options?.externalSearch, externalSearch, search]);

  const sortedRows = useMemo(() => {
    if (sortColumn === null) return filteredRows;
    
    return [...filteredRows].sort((a, b) => {
      const column = columns[sortColumn];
      const aVal = column.textValue ? column.textValue(a) : column.getter(a);
      const bVal = column.textValue ? column.textValue(b) : column.getter(b);
      
      const aStr = (aVal ?? '').toString().toLowerCase();
      const bStr = (bVal ?? '').toString().toLowerCase();
      
      const comparison = aStr.localeCompare(bStr);
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [filteredRows, sortColumn, sortOrder, columns]);

  const handleSort = (columnIndex) => {
    if (!columns[columnIndex].sortable) return;
    
    if (sortColumn === columnIndex) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnIndex);
      setSortOrder('asc');
    }
  };

  const hasActionsColumn = actions.delete || (actions.extra && actions.extra.length > 0);
  const columnCount = columns.length + (hasActionsColumn ? 1 : 0) + (selection ? 1 : 0);

  const opts = {...defaultOptions, ...options};
  const bodyRef = useRef(null);

  // Extra props are how the virtualizer attaches its measuring ref; the plain
  // path passes none.
  const renderRow = useCallback((row, rowProps) => (
    <tr
      {...rowProps}
      key={row.id}
      onClick={() => { if (!actions.noPrimary) { navigate(actions.get.replaceAll(':id', row.id)) } } }
      style={ actions.noPrimary ? {} : {cursor: 'pointer'}}
    >
      {selection && <td style={checkboxCellStyle} onClick={(e) => e.stopPropagation()}>
        <Checkbox
          size="sm"
          sx={checkboxSx}
          checked={selection.selectedIds.has(row.id)}
          onChange={() => selection.onToggleRow(row.id)}
        />
      </td>}
      {columns.map((column) => <td key={column.label} style={cellStyle(column)}>{column.getter(row)}</td>)}
      {hasActionsColumn && <td style={{ width: '90px', minWidth: '90px', whiteSpace: 'nowrap' }}>
        {actions.extra && actions.extra.map((action) =>
          <Fragment key={action.type}>
            {(action.display ? action.display(row) : true) &&
              (action.render ? action.render(row) : <ExtraAction
                to={`${row.id}/${action.type}`}
                onClick={action.onClick ? () => action.onClick(row) : null}
                tooltip={action.tooltip}
                icon={action.icon} />)}
          </Fragment>
        )}
        {actions.edit && actions.edit(row) && <EditAction to={`${row.id}/update`}/>}
        {actions.delete && <DeleteAction id={row.id} name={primaryTextValue(row)} useDelete={actions.delete}/>}
      </td>}
    </tr>
  ), [actions, columns, hasActionsColumn, navigate, selection, primaryTextValue]);

  const allVisibleSelected = selection && sortedRows.length > 0 && sortedRows.every((row) => selection.selectedIds.has(row.id));
  const someVisibleSelected = selection && sortedRows.some((row) => selection.selectedIds.has(row.id));

  return <Box>
    {(opts.searchable || opts.primaryButton || actions.create) && <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        gap: 2,
        py: 2,
      }}
    >
      {opts.searchable && <Input
        sx={{width: '250px'}}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search"
        startDecorator={<SearchIcon/>}
      />}
      {actions.create && <Link to={actions.create}><Button startDecorator={<AddIcon/>}>Create {capitalizeFirstLetter(names.singular)}</Button></Link>}
      {opts.primaryButton && opts.primaryButton}
    </Box>}

    {error ?
      <ApiError error={error}/> :
      <Sheet
        variant="outlined"
        sx={{
          borderRadius: 'sm',
          overflowX: 'auto',
        }}
      >

        <Table color="neutral" variant="plain" stripe="odd" hoverRow sx={{
          '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
          '--Table-headerUnderlineThickness': '1px',
          '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
          '--TableCell-paddingY': '4px',
          '--TableCell-paddingX': '8px',
          '--TableRow-stripeBackground': '#fff',
        }}>
          {opts.header && <thead>
          <tr>
            {selection && <th style={checkboxCellStyle}>
              <Checkbox
                size="sm"
                sx={checkboxSx}
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected && !allVisibleSelected}
                onChange={() => selection.onToggleAll(sortedRows.map((row) => row.id))}
              />
            </th>}
            {columns.map((column, index) => (
              <th
                key={column.label}
                onClick={() => column.sortable && handleSort(index)}
                style={{ ...cellStyle(column), ...(column.sortable ? { cursor: 'pointer', userSelect: 'none' } : {}) }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {column.label}
                  {column.sortable && sortColumn === index && (
                    sortOrder === 'asc' ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />
                  )}
                </Box>
              </th>
            ))}
            {hasActionsColumn && <th style={{ width: '90px', minWidth: '90px', whiteSpace: 'nowrap' }}>Actions</th>}
          </tr>
          </thead>}
          {opts.customHeader && <thead><tr>
            <th colSpan={columnCount}>{opts.customHeader}</th>
          </tr></thead>}
          <tbody ref={bodyRef}>
          {isLoading ? <RowsSkeleton columnCount={columnCount} hasSelection={!!selection}/> : <>
            {opts.virtualize && sortedRows.length > 0
              ? <VirtualRows rows={sortedRows} renderRow={renderRow} columnCount={columnCount} bodyRef={bodyRef}/>
              : sortedRows.map((row) => renderRow(row))}
            {rows.length > 0 && sortedRows.length === 0 && <tr>
              <td colSpan={columnCount}>There are no matching {names.plural}</td>
            </tr>}
            {rows.length === 0 && !isLoading && <tr>
              <td colSpan={columnCount}>There are no {names.plural}</td>
            </tr>}
          </>}
          </tbody>
        </Table>
      </Sheet>
    }
  </Box>;
}

// One line of text plus the cell padding, as measured in the browser. Rows are
// re-measured for real once rendered, so this only has to be close — but the
// closer it is, the less the scrollbar drifts while scrolling a long list.
const ESTIMATED_ROW_HEIGHT = 41;

// Renders only the rows near the viewport, with a spacer row above and below
// standing in for the rest. Spacers keep this inside a real <table>, so column
// widths still line up with the header — absolutely positioned rows would not.
function VirtualRows({rows, renderRow, columnCount, bodyRef}) {
  const [scrollMargin, setScrollMargin] = useState(0);

  // Re-measured rather than read once: anything appearing above the table (the
  // selection bar, a filter chip wrapping to a new line) moves it down, and a
  // stale offset puts every row in the wrong place.
  useLayoutEffect(() => {
    const measure = () => {
      if (!bodyRef.current) return;
      const top = bodyRef.current.getBoundingClientRect().top + window.scrollY;
      setScrollMargin((previous) => (previous === top ? previous : top));
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [bodyRef]);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 10,
    scrollMargin,
  });

  const virtualRows = virtualizer.getVirtualItems();
  if (virtualRows.length === 0) return null;

  const paddingTop = virtualRows[0].start - scrollMargin;
  const paddingBottom = virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end;

  return <>
    <tr aria-hidden><td colSpan={columnCount} style={spacerCellStyle(paddingTop)}/></tr>
    {virtualRows.map((virtualRow) => renderRow(rows[virtualRow.index], {
      'data-index': virtualRow.index,
      ref: virtualizer.measureElement,
    }))}
    <tr aria-hidden><td colSpan={columnCount} style={spacerCellStyle(paddingBottom)}/></tr>
  </>;
}

// The height has to sit on the cell, not the row: Joy gives every td a
// `height: var(--unstable_TableCell-height)`, which beats a height on the tr and
// stops the spacers collapsing below one row.
function spacerCellStyle(height) {
  return {height: Math.max(0, height), padding: 0, border: 0};
}

function RowsSkeleton({columnCount, hasSelection}) {
  return [...Array(5)].map((_, rowIndex) => <tr key={rowIndex}>
    {[...Array(columnCount)].map((_, cellIndex) => {
      // The selection column holds a checkbox, not text, so its placeholder has
      // to be the size of the checkbox rather than the width of the cell.
      const isCheckboxCell = hasSelection && cellIndex === 0;
      return <td key={cellIndex} style={isCheckboxCell ? checkboxCellStyle : undefined}>
        {isCheckboxCell
          ? <Skeleton variant="rectangular" sx={checkboxSkeletonSx}/>
          : <Skeleton variant="text"/>}
      </td>;
    })}
  </tr>);
}

const tdStyle = {
  maxWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

// A column can request a fixed width (e.g. a narrow numeric column, so the
// name column can take the space back) and/or its own cell style overrides
// (e.g. tighter padding to match). Setting an explicit width replaces the
// maxWidth: 0 trick above -- that trick only exists to force ellipsis on a
// column with no width of its own.
function cellStyle(column) {
  const widthStyle = column.width
    ? { maxWidth: column.width, width: column.width, minWidth: column.width }
    : {};
  return { ...tdStyle, ...widthStyle, ...column.cellStyle };
}

const checkboxCellStyle = {
  width: '40px',
  verticalAlign: 'middle',
};

// A checkbox on its own in a cell would otherwise sit on the text baseline,
// which leaves the row's line-height as uneven space above and below it. Making
// it a block-level flex container drops the baseline out of the picture, so the
// cell's own symmetric padding is all that remains.
const checkboxSx = {
  display: 'flex',
  alignItems: 'center',
};

// Matches the sm checkbox the skeleton stands in for (--Checkbox-size).
const checkboxSkeletonSx = {
  width: '1rem',
  height: '1rem',
  borderRadius: '0.25rem',
};
